import React, { useMemo } from 'react';
import { Button, Col, Divider, Modal, Row, Space, Typography } from 'antd';
import { SendOutlined } from '@ant-design/icons';

const { Text } = Typography;

interface EnvioTraduccionesProps {
  open: boolean;
  setOpen: (open: boolean) => void;
  selectedRows: any[];
  onConfirm: () => void;
}

interface ResumenSolicitud {
  idSolicitud: number;
  solicitante: string;
  totalProcesadas: number;
  sinObservaciones: number;
  conObservaciones: number;
  sinProcesar: number;
}

const EnvioTraducciones: React.FC<EnvioTraduccionesProps> = ({
  open,
  setOpen,
  selectedRows,
  onConfirm,
}) => {

  // Agrupa las filas seleccionadas por solicitante + idSolicitud para el resumen
  const resumen = useMemo<ResumenSolicitud[]>(() => {
    const map = new Map<string, ResumenSolicitud>();

    selectedRows.forEach(row => {
      const key = `${row.solicitante}-${row.id}`;
      if (!map.has(key)) {
        map.set(key, {
          idSolicitud:      row.id,
          solicitante:      row.solicitante,
          totalProcesadas:  0,
          sinObservaciones: 0,
          conObservaciones: 0,
          sinProcesar:      0,
        });
      }
      const entry = map.get(key)!;
      const procesada = row.estado === 'Traducida';
      if (procesada) {
        entry.totalProcesadas += 1;
        if (row.obsTraductor) {
          entry.conObservaciones += 1;
        } else {
          entry.sinObservaciones += 1;
        }
      } else {
        entry.sinProcesar += 1;
      }
    });

    return Array.from(map.values());
  }, [selectedRows]);

  // Agrupa el resumen por solicitante para el encabezado "Se notificarán X comunicaciones a: Nombre"
  const porSolicitante = useMemo(() => {
    const map = new Map<string, ResumenSolicitud[]>();
    resumen.forEach(r => {
      if (!map.has(r.solicitante)) map.set(r.solicitante, []);
      map.get(r.solicitante)!.push(r);
    });
    return Array.from(map.entries());
  }, [resumen]);

  return (
    <>
      <Button
        icon={<SendOutlined />}
        disabled={selectedRows.length === 0}
        onClick={() => setOpen(true)}
      >
        Envío traducciones
      </Button>

      <Modal
        centered
        width={560}
        title="Envío traducciones"
        open={open}
        onCancel={() => setOpen(false)}
        footer={
          <Row justify="end">
            <Col>
              <Space>
                <Button shape="round" type="text" onClick={() => setOpen(false)}>
                  Cancelar
                </Button>
                <Button shape="round" type="primary" onClick={onConfirm}>
                  Aceptar
                </Button>
              </Space>
            </Col>
          </Row>
        }
      >
        <Row gutter={[0, 12]}>
          {porSolicitante.map(([solicitante, solicitudes]) => {
            const totalNotificar = solicitudes.reduce((s, r) => s + r.totalProcesadas, 0);
            return (
              <Col span={24} key={solicitante}>
                <Text strong>
                  Se notificarán {totalNotificar} comunicaciones procesadas a: {solicitante}
                </Text>
                {solicitudes.map(r => (
                  <Row key={r.idSolicitud} style={{ paddingTop: 6, paddingLeft: 8 }}>
                    <Col span={24}><Text>Id solicitud: {r.idSolicitud}</Text></Col>
                    <Col span={24}><Text>Comunicaciones sin observaciones: {r.sinObservaciones}</Text></Col>
                    <Col span={24}><Text>Comunicaciones con observaciones: {r.conObservaciones}</Text></Col>
                    <Col span={24}><Text>Total de Comunicaciones procesadas: {r.totalProcesadas}</Text></Col>
                    <Col span={24}><Text>Total de Comunicaciones sin procesar: {r.sinProcesar}</Text></Col>
                  </Row>
                ))}
                <Divider style={{ margin: '10px 0' }} />
              </Col>
            );
          })}

          <Col span={24}>
            <Text strong>¿Está seguro que desea realizar esta acción?</Text>
          </Col>
        </Row>
      </Modal>
    </>
  );
};

export default EnvioTraducciones;
